import { Link } from 'react-router-dom';
import { ClipboardCheck, FileSearch, Shield, Truck, UserCheck, Users, GraduationCap } from 'lucide-react';
import SEO from '../components/shared/SEO';

const ServiceApprovalPolicyPage = () => {
  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="service-approval-policy-page">
      <SEO 
        title="Service Approval Policy | Queen B Logistics"
        description="Service Approval Policy for Queen B Logistics - Learn about our application review and service activation process."
      />
      
      {/* Header */}
      <section className="py-16 bg-[#050505] border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-4">
            <ClipboardCheck className="w-8 h-8 text-[#D4AF37]" />
            <span className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest">Legal</span>
          </div>
          <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mb-4">
            Service Approval Policy
          </h1>
          <p className="text-white/60">
            Queen B Logistics Service Activation Policy
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-invert max-w-none">
            
            <PolicySection number="1" title="Application & Review" icon={FileSearch}>
              <p>All services are subject to:</p>
              <ul>
                <li>Authority verification</li>
                <li>Compliance review</li>
                <li>Operational assessment</li>
                <li>Capacity availability</li>
              </ul>
              <div className="bg-amber-500/10 border border-amber-500/30 p-4 my-4">
                <p className="text-amber-400 m-0"><strong>Payment does not automatically guarantee service activation.</strong></p>
              </div>
            </PolicySection>

            <PolicySection number="2" title="Signature Program Activation" icon={UserCheck}>
              <p>Signature Program begins after:</p>
              <ul>
                <li>Payment confirmation</li>
                <li>Application review</li>
                <li>Document verification</li>
              </ul>
            </PolicySection>

            <PolicySection number="3" title="Compliance Monitoring Activation" icon={Shield}>
              <p>Monitoring services begin only after:</p>
              <ul>
                <li>Authority is active</li>
                <li>Client selects a plan</li>
                <li>Billing authorization is confirmed</li>
              </ul>
            </PolicySection>

            <PolicySection number="4" title="Dispatch Activation" icon={Truck}>
              <p>Dispatch services begin only after:</p>
              <ul>
                <li>Carrier onboarding review</li>
                <li>Insurance verification</li>
                <li>Authority validation</li>
                <li>Signed service agreement</li>
              </ul>
              <p className="text-white/50 italic">If application is not approved, refund may be issued at company discretion.</p>
            </PolicySection>

            <PolicySection number="5" title="Selective Acceptance" icon={Users}>
              <p>Queen B Logistics works with a <strong className="text-[#D4AF37]">limited number of carriers</strong> to ensure quality and performance.</p>
              <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-4 my-4">
                <p className="text-[#D4AF37] m-0 font-bold">We reserve the right to decline service.</p>
              </div>
            </PolicySection>

            <PolicySection number="6" title="Queen B Academy Access" icon={GraduationCap}>
              <p>Queen B Academy courses are available to anyone — no application or approval required.</p>
              <ul>
                <li>Academy is a <strong>standalone educational product</strong></li>
                <li>No carrier authority or Hive membership required to purchase</li>
                <li>Access is granted immediately upon payment</li>
                <li>Access codes are delivered via email</li>
              </ul>
              <div className="bg-green-500/10 border border-green-500/30 p-4 my-4">
                <p className="text-green-400 m-0 font-bold">Academy courses are open to the public with no approval process.</p>
              </div>
              <p className="text-white/50">Note: All Academy purchases are final and non-refundable.</p>
            </PolicySection>

          </div>

          {/* Back Link */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <Link to="/" className="text-[#D4AF37] hover:underline">
              ← Back to Home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const PolicySection = ({ number, title, icon: Icon, children }) => (
  <div className="mb-12">
    <div className="flex items-center gap-3 mb-4">
      <span className="w-8 h-8 bg-[#D4AF37]/20 text-[#D4AF37] flex items-center justify-center font-bold text-sm">
        {number}
      </span>
      <h2 className="font-['Barlow_Condensed'] text-2xl font-bold text-white uppercase m-0">
        {title}
      </h2>
      {Icon && <Icon className="w-5 h-5 text-[#D4AF37]" />}
    </div>
    <div className="text-white/70 leading-relaxed pl-11">
      {children}
    </div>
  </div>
);

export default ServiceApprovalPolicyPage;
